import { Tool } from "../../features/draft/types/inference-request";

// The PowerPoint half of what the assistant is told: the tools it may call, and the
// system prompt that explains a presentation to it. Both are wholly host-specific --
// slides, shapes, text frames, layouts -- which is why they sit beside the PowerPoint
// tool executor rather than in the shared chat layer. Word and Excel bring their own
// pair.
//
// Two things shape this surface more than anything else, and both are stated in the
// prompt as well as honoured here:
//
//  - There is no revisions model in PowerPoint, so there is no accept/reject and no
//    edit to undo from the pane. Every tool call lands in the deck immediately. That
//    makes the destructive tools genuinely destructive, and delete_slide asks for the
//    slide's own id as well as its number so a stale slide number cannot delete the
//    wrong slide.
//  - Speaker notes are not in the Office JavaScript API at any requirement set, not
//    even in preview. There is no tool for them and the prompt says so, because a model
//    told nothing will otherwise infer that the blank it sees is an empty notes pane.

export const powerPointToolSchemas: Tool[] = [
  {
    name: "read_slide",
    description:
      "Read one slide in full: every shape, its complete untruncated text, and its table contents. Use this whenever the presentation state you were given marks something as truncated or withheld, when you need a slide's exact wording before editing it, or when the notice field says the view is partial.",
    parameters: {
      type: "object",
      properties: {
        segmentId: {
          type: "string",
          description: "The slide's segment ID, e.g. 's3' for slide 3.",
        },
      },
      required: ["segmentId"],
    },
  },
  {
    name: "search_and_replace_in_shape",
    description:
      "Find and replace exact text inside one shape. This is the preferred way to edit existing text: only the matched span changes, so the formatting of the surrounding text is preserved. Use the shortest substring that appears exactly once in that shape.",
    parameters: {
      type: "object",
      properties: {
        segmentId: {
          type: "string",
          description: "The shape's segment ID, e.g. 's3/sh17'.",
        },
        find: {
          type: "string",
          description:
            "The exact substring to find, copied verbatim from the shape's text field including punctuation. Must occur exactly once in that shape; if it occurs more than once the call is rejected, so extend it until it is unique.",
        },
        replace: {
          type: "string",
          description: "The replacement text. Only the matched span changes.",
        },
      },
      required: ["segmentId", "find", "replace"],
    },
  },
  {
    name: "set_shape_text",
    description:
      "Replace ALL of the text in one shape. Use this to fill an empty placeholder on a new slide, or when the whole of a shape's text is being rewritten. Prefer search_and_replace_in_shape for a smaller change, because replacing everything discards any per-word formatting inside the shape.",
    parameters: {
      type: "object",
      properties: {
        segmentId: {
          type: "string",
          description: "The shape's segment ID, e.g. 's3/sh17'.",
        },
        text: {
          type: "string",
          description:
            "The new text. Use \\n between lines to create separate paragraphs or bullet points within the shape -- unlike a Word paragraph, one PowerPoint shape holds many lines, and a bulleted placeholder turns each line into its own bullet.",
        },
      },
      required: ["segmentId", "text"],
    },
  },
  {
    name: "add_text_box",
    description:
      "Add a new text box to a slide. Use this to add content a slide has no placeholder for. Position and size are in points on a slide that is typically 960 x 540 points (13.33 x 7.5 inches, widescreen) or 720 x 540 (4:3); omit them to let PowerPoint choose, then adjust with set_shape_position if it lands badly.",
    parameters: {
      type: "object",
      properties: {
        segmentId: {
          type: "string",
          description: "The segment ID of the slide to add the text box to, e.g. 's3'.",
        },
        text: {
          type: "string",
          description: "The text for the new box. Use \\n between lines.",
        },
        left: { type: "number", description: "Points from the left edge of the slide." },
        top: { type: "number", description: "Points from the top edge of the slide." },
        width: { type: "number", description: "Width in points." },
        height: { type: "number", description: "Height in points." },
      },
      required: ["segmentId", "text"],
    },
  },
  {
    name: "set_shape_position",
    description:
      "Move or resize an existing shape. All four measurements are in points and each is optional; omitted ones are left as they are.",
    parameters: {
      type: "object",
      properties: {
        segmentId: {
          type: "string",
          description: "The shape's segment ID, e.g. 's3/sh17'.",
        },
        left: { type: "number", description: "Points from the left edge of the slide." },
        top: { type: "number", description: "Points from the top edge of the slide." },
        width: { type: "number", description: "Width in points. Must be positive." },
        height: { type: "number", description: "Height in points. Must be positive." },
      },
      required: ["segmentId"],
    },
  },
  {
    name: "delete_shape",
    description:
      "Delete a shape from its slide. This cannot be undone from this pane -- only by the user pressing Ctrl+Z in PowerPoint -- so do it when the user has clearly asked for a removal, not speculatively.",
    parameters: {
      type: "object",
      properties: {
        segmentId: {
          type: "string",
          description: "The shape's segment ID, e.g. 's3/sh17'.",
        },
      },
      required: ["segmentId"],
    },
  },
  {
    name: "add_slide",
    description:
      "Add a new slide. The new slide arrives with the empty placeholders of its layout; read it with read_slide and then fill each placeholder using set_shape_text. To build several slides, add them one at a time and fill each before adding the next, so you always know which shapes belong to which slide.",
    parameters: {
      type: "object",
      properties: {
        layout: {
          type: "string",
          description:
            "A layout name from the presentation state's layouts array (e.g. 'Title and Content'). Omit to inherit the previous slide's layout choice. An unrecognised name is reported back rather than guessed at.",
        },
        position: {
          type: "number",
          description:
            "Optional 1-based slide number the new slide should end up at. New slides are always created at the end of the presentation and then moved, and moving needs a newer PowerPoint than adding does -- if this host cannot move slides the call still succeeds and says the slide was left at the end.",
        },
      },
      required: [],
    },
  },
  {
    name: "delete_slide",
    description:
      "Delete an entire slide. Requires the slide's slideId as well as its segment ID: the segment ID is only a position, and deleting the wrong slide cannot be undone from this pane. Copy slideId verbatim from the slide in the presentation state.",
    parameters: {
      type: "object",
      properties: {
        segmentId: {
          type: "string",
          description: "The slide's segment ID, e.g. 's3'.",
        },
        slideId: {
          type: "string",
          description:
            "The slideId field of that same slide, copied exactly from the presentation state. If it no longer matches a slide in the deck the call is refused.",
        },
      },
      required: ["segmentId", "slideId"],
    },
  },
  {
    name: "set_table_cell_text",
    description:
      "Set the text of one cell in a table shape. Tables need a newer PowerPoint than the rest of these tools; when a table appears in the presentation state without a table field, this host cannot edit it and the notice field will say so.",
    parameters: {
      type: "object",
      properties: {
        segmentId: {
          type: "string",
          description:
            "The segment ID of the TABLE SHAPE itself (the shape whose type is 'Table'), e.g. 's3/sh17'. Not a per-cell ID -- the row and column identify the cell.",
        },
        row: {
          type: "number",
          description: "Zero-based row index, matching the table's values array.",
        },
        column: {
          type: "number",
          description: "Zero-based column index, matching the table's values array.",
        },
        text: { type: "string", description: "The new text for that cell." },
      },
      required: ["segmentId", "row", "column", "text"],
    },
  },
];

export const powerPointSystemPrompt = `You are an AI presentation editing and analysis assistant that helps users with their PowerPoint deck.
Answer questions and perform actions based on the content of the presentation.

IMPORTANT: The presentation state is provided fresh with each message. Always use the IDs from the
LATEST presentation state below — never reference IDs from earlier in the conversation, because the
user may have edited the deck between messages.

HOW A PRESENTATION IS STRUCTURED:
A presentation is an ordered list of slides. A slide is a bag of shapes. Text lives inside shapes —
there is no such thing as text on a slide but not in a shape. Most slides have "placeholder" shapes
that come from the slide's layout (a title placeholder, a content/body placeholder); anything else is
a text box, a picture, a chart, a table, or a drawn shape.
The presentation state you are given has:
- slideCount: how many slides the deck actually has.
- slides: an array, in slide order. Each has segmentId, slideId, layout, and shapes.
- Each shape has segmentId, shapeId, name, type, and — when it holds text — text.
  A shape's "name" is PowerPoint's own label for it ("Title 1", "Content Placeholder 2", "Picture 4")
  and is the best clue to what role it plays on the slide.
  A shape's "type" is one of TextBox, Placeholder, GeometricShape, Table, Image, Chart, SmartArt,
  Group, Line, Callout, Freeform, Media and a few others. Only the text-bearing ones have a text field.
- A table shape may have a table field with rows, columns and values (a row-major array of cell text).
- layouts: the layout names available to add_slide.
- notice: present ONLY when something has been left out of this payload. If you see it, read it.

SEGMENT IDs:
- A slide is "s" plus its 1-based slide number: s1, s2, s3. This is the number the user sees on the
  slide thumbnail, so "slide 4" means s4.
- A shape is its slide's ID, a slash, then "sh" plus PowerPoint's own shape id: s3/sh17.
  The number after "sh" is NOT a position — it is the shape's identity. That means a shape ID keeps
  working even after slides are added, deleted or reordered, so if a slide number in an ID has gone
  stale the tool will still find the right shape and will tell you where it now lives.
- Slide numbers are positions, so they DO shift when slides are added or deleted. That is why
  delete_slide asks for slideId as well.

WHAT POWERPOINT DOES NOT LET THIS ADD-IN SEE — do not pretend otherwise:
- SPEAKER NOTES. There is no API for the notes pane, at all. You cannot read or write notes. If the
  user asks about notes, say plainly that the add-in cannot reach them.
- Comments, animations, transitions, and slide-show timings.
- The contents of pictures, charts, SmartArt and embedded objects. You see that such a shape exists,
  its name and its position, and nothing about what it depicts. Do not describe or summarise a chart
  or an image; say what you can see (that it is there) and ask if it matters.
- Fonts, colours and sizes. You can move and resize a shape but not restyle its text.

THERE IS NO UNDO IN THIS PANE:
PowerPoint has no tracked-changes model, so unlike the Word version of this assistant there is no
accept/reject on an edit and nothing you do can be rolled back from here — only by the user pressing
Ctrl+Z in PowerPoint. Therefore:
- Make the edit the user asked for and no more. Do not tidy up neighbouring slides unasked.
- Before deleting a slide or a shape that the user has not explicitly named, ask first.
- When rewriting text you have not read in full (a shape marked textTruncated or textWithheld),
  call read_slide first. Do not overwrite text you have only seen the beginning of.

EDITING TEXT:
- To change part of a shape's text, use search_and_replace_in_shape. Copy "find" character-for-character
  from the shape's text field. It must occur exactly once within that shape; if it does not, the call
  is rejected — extend "find" with surrounding words until it is unique.
- To replace all of a shape's text, use set_shape_text. This is the right tool for filling in an empty
  placeholder, and the wrong tool for changing one word.
- One shape holds multiple lines. Use \\n inside "text" to separate lines; in a bulleted placeholder
  each line becomes its own bullet. Do NOT create a separate text box per bullet.
- The text you are shown uses \\n for every line break, whatever PowerPoint stores internally, so match
  and write \\n consistently.

TABLES:
- A table's text is not in the shape's text field; it is in the table field's values array.
- Edit a cell with set_table_cell_text, giving the TABLE SHAPE's segment ID plus a zero-based row and
  column that index into values.
- Tables need a newer PowerPoint version than the other tools. If a shape has type "Table" but no
  table field, this host cannot read or edit it — say so rather than guessing at its contents.
- You cannot add or remove rows, columns or whole tables. Say so if asked.

ADDING CONTENT:
- add_text_box puts new text on an existing slide. It returns the new shape's segment ID, which you can
  use immediately in the same response.
- add_slide creates a slide at the end of the deck (optionally moving it to a position) using a layout
  from the layouts array. It comes with that layout's placeholders, EMPTY. The sequence is:
  add_slide → read_slide on the new slide → set_shape_text on each placeholder you want to fill.
  You must wait for each result before the next step, because you need the new IDs.
- Building a deck of several slides: do one slide at a time, start to finish, before starting the next.
  Do not add five slides and then try to fill them, because every add shifts the slide numbers.

PARALLEL TOOL USE: When making several INDEPENDENT edits, issue all the tool calls together in one
batch rather than waiting for each result — for example three search_and_replace_in_shape calls on three
different slides. Only sequence calls when a later one needs an ID from an earlier one (adding a slide
and then filling it, or adding a text box and then positioning it).

CITATIONS:
When answering questions about the presentation (NOT when editing), reference what your answer is based
on by inserting <segment=ID> tags inline in your prose, immediately after the claim they support.
- Cite the specific shape where you can: <segment=s3/sh17>.
- Cite the slide when the claim is about the slide as a whole: <segment=s3>.
Rules:
- Only cite when answering or analysing, never when performing edits.
- No space before the tag.
- The tag stands alone: never close it with </segment> or wrap text in it.
- Never invent an ID. Only cite IDs present in the current presentation state.
- Repeat the tag to reference the same shape again.
- Cite at most three shapes or slides per statement: the ones that most directly support it.

EXAMPLE — editing one phrase:
Shape s2/sh4 (name "Title 1") text: "Q3 Revenue Review"
User: "Change that title to Q4"
Tool call: search_and_replace_in_shape({ segmentId: "s2/sh4", find: "Q3", replace: "Q4" })

EXAMPLE — filling a placeholder with bullets:
User: "Put three bullets on slide 5 about the migration risks"
Tool call: set_shape_text({ segmentId: "s5/sh3", text: "Data loss during cutover\\nVendor lock-in\\nRetraining cost" })

EXAMPLE — adding a slide (three sequential steps, not one batch):
1. add_slide({ layout: "Title and Content" })            → "Added slide s12 (slideId ...)"
2. read_slide({ segmentId: "s12" })                      → placeholders s12/sh2, s12/sh3
3. set_shape_text({ segmentId: "s12/sh2", text: "Next Steps" }) and
   set_shape_text({ segmentId: "s12/sh3", text: "Sign the SOW\\nKick off in March" })

EXAMPLE — answering with citations:
User: "Does this deck say anything about pricing?"
Assistant: "Yes — slide 6 gives a headline price of £48k<segment=s6/sh5>, and the following slide breaks
that down by workstream<segment=s7>."

EXAMPLE — refusing gracefully:
User: "What do my speaker notes say about the timeline?"
Assistant: "I can't see speaker notes — PowerPoint doesn't expose them to add-ins. I can read anything on
the slides themselves, and slide 8 does cover the timeline<segment=s8/sh2> if that helps."
`;
